import { Strategy, Block, TradingPlatform, AssetClass } from '../types';
import { logger } from '../utils/logger';
import { supabase } from './supabaseClient';
import { checkTslBlockShape } from '../engine/tslBlock/astGuard';

interface StrategyMetadata {
  name: string;
  description: string;
  platform: TradingPlatform;
  assetClass: AssetClass;
  version: string;
  tags: string[];
  isPublic?: boolean;
}

// ── P9.2B — structural-lineage persistence guard ─────────────────────────────
// A structural_setup_v1 strategy must NEVER persist with incomplete lineage, and its
// dedup fingerprints persist as tags (the production research-memory read surface,
// author-scoped). Legacy strategies are untouched (no markers ⇒ no-op).
const REQUIRED_LINEAGE_FIELDS = [
  'profileId', 'profileVersion', 'ribPropositionId', 'setupIrId', 'memoryDisposition',
  'generatorVersion', 'compilerVersion', 'rawBlockCount', 'semanticBlockCount',
  'requiredData', 'evidenceState', 'commercialTargetState', 'generatedAt', 'behavioralFingerprint',
] as const;

interface GenerationLineage {
  profileId?: string; ribPropositionId?: string; behavioralFingerprint?: string; ribSlug?: string;
  [k: string]: unknown;
}

function structuralLineageOf(blocks: Block[]): GenerationLineage | null {
  const cfg = blocks.find((b) => String(b.type) === 'strategy_config');
  const l = (cfg?.data as { generationLineage?: GenerationLineage } | undefined)?.generationLineage;
  return l && typeof l === 'object' ? l : null;
}

/** Throws persistence_failed when lineage is incomplete; returns dedup tags to persist. */
async function structuralSaveGuard(blocks: Block[], userId: string): Promise<string[]> {
  const l = structuralLineageOf(blocks);
  const hasStructuralBlocks = blocks.some((b) => String(b.type) === 'setup_state');
  if (!l) {
    if (hasStructuralBlocks) {
      throw new Error('persistence_failed: structural strategy has no generationLineage — refusing to persist an incomplete strategy');
    }
    return []; // legacy strategy — untouched
  }
  const missing = REQUIRED_LINEAGE_FIELDS.filter((f) => l[f] === undefined || l[f] === null || l[f] === '');
  if (missing.length) {
    throw new Error(`persistence_failed: required lineage missing (${missing.join(', ')}) — refusing to persist an incomplete strategy`);
  }
  const dedupTag = `sirdedup:prop_${l.ribPropositionId}|${l.behavioralFingerprint}`;
  // pre-insert duplicate check (deterministic; bounds the concurrent-duplicate window —
  // full serialization would need a DB uniqueness migration, deliberately out of scope)
  const { data: dupes, error } = await supabase
    .from('strategy_templates').select('id').eq('author_id', userId).contains('tags', [dedupTag]).limit(1);
  if (error) throw new Error(`persistence_failed: duplicate check unavailable (${error.message}) — refusing to persist unverified`);
  if (dupes && dupes.length > 0) {
    throw new Error('persistence_failed: an identical economic behavior is already saved (duplicate strategy) — research memory rejected the duplicate save');
  }
  return [
    'profile:structural_setup_v1',
    ...(l.ribSlug ? [`rib:${l.ribSlug}`] : []),
    dedupTag,
    `sirbeh:${l.behavioralFingerprint}`,
  ];
}

export const saveStrategy = async (
  blocks: Block[],
  metadata: StrategyMetadata
): Promise<string> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to save strategy');

    // P9.2B — refuse incomplete structural lineage; collect dedup tags for memory reads
    const lineageTags = await structuralSaveGuard(blocks, user.id);
    if (lineageTags.length) metadata = { ...metadata, tags: [...new Set([...(metadata.tags ?? []), ...lineageTags])] };

    logger.debug('Saving strategy', { userId: user.id, metadata, blockCount: blocks.length });

    // Instead of saving to strategies table, save to strategy_templates
    const { data, error } = await supabase
      .from('strategy_templates')
      .insert({
        author_id: user.id,
        name: metadata.name,
        description: metadata.description,
        blocks,
        platform: metadata.platform,
        asset_class: metadata.assetClass,
        version: metadata.version,
        tags: metadata.tags,
        is_public: metadata.isPublic || false,
        category: 'custom', // Default category for user-created strategies
        difficulty: 'intermediate', // Default difficulty
      })
      .select('id')
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    if (!data) {
      throw new Error('No data returned from insert operation');
    }

    return data.id;
  } catch (error) {
    console.error('Strategy save error:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to save strategy'
    );
  }
};

// Partial-update — patches ONLY the blocks column. Use when the caller has
// new blocks but doesn't want to (or doesn't have) the full metadata to
// reassert on every save (e.g. the prop-firm "Apply to loaded strategy"
// one-click action — it only touches strategy_config fields, NOT name /
// description / platform / tags). Returns once Supabase acknowledges the
// write; throws on auth-missing or DB error.
export const updateStrategyBlocks = async (
  id: string,
  blocks: Block[],
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to update strategy');
    const { error } = await supabase
      .from('strategy_templates')
      .update({ blocks })
      .eq('id', id)
      .eq('author_id', user.id);
    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update strategy blocks'
    );
  }
};

export const updateStrategy = async (
  id: string,
  blocks: Block[],
  metadata: StrategyMetadata
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to update strategy');

    // Update strategy_templates instead of strategies
    const { error } = await supabase
      .from('strategy_templates')
      .update({
        name: metadata.name,
        description: metadata.description,
        blocks,
        platform: metadata.platform,
        asset_class: metadata.assetClass,
        version: metadata.version,
        tags: metadata.tags,
        is_public: metadata.isPublic || false,
      })
      .eq('id', id)
      .eq('author_id', user.id);

    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to update strategy'
    );
  }
};

export const loadStrategy = async (id: string): Promise<Strategy> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to load strategy');

    // Load from strategy_templates instead of strategies
    const { data, error } = await supabase
      .from('strategy_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error loading strategy:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Strategy not found');
    }

    // Check if the user has access to this strategy
    if (!data.is_public && data.author_id !== user.id) {
      throw new Error('You do not have permission to access this strategy');
    }

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      blocks: data.blocks,
      platform: data.platform,
      assetClass: data.asset_class,
      created: new Date(data.created_at),
      updated: new Date(data.updated_at),
      version: data.version,
      tags: data.tags,
      isPublic: data.is_public, // Add this line
    };
  } catch (error) {
    console.error('Error in loadStrategy:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to load strategy'
    );
  }
};

export const listStrategies = async (): Promise<Strategy[]> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to list strategies');

    // List from strategy_templates instead of strategies
    const { data, error } = await supabase
      .from('strategy_templates')
      .select('*')
      .or(`author_id.eq.${user.id},is_public.eq.true`)
      .order('updated_at', { ascending: false });

    if (error) throw error;

    return data.map((strategy) => ({
      id: strategy.id,
      name: strategy.name,
      description: strategy.description,
      blocks: strategy.blocks,
      platform: strategy.platform,
      assetClass: strategy.asset_class,
      created: new Date(strategy.created_at),
      updated: new Date(strategy.updated_at),
      version: strategy.version,
      tags: strategy.tags,
      isPublic: strategy.is_public, // Add this line
    }));
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to list strategies'
    );
  }
};

export const deleteStrategy = async (id: string): Promise<void> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to delete strategy');

    // Delete from strategy_templates instead of strategies
    const { error } = await supabase
      .from('strategy_templates')
      .delete()
      .eq('id', id)
      .eq('author_id', user.id);

    if (error) throw error;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'Failed to delete strategy'
    );
  }
};

export const exportStrategy = (
  blocks: Block[],
  metadata?: Partial<StrategyMetadata>
): string => {
  const strategy: Strategy = {
    id: crypto.randomUUID(),
    name: metadata?.name || 'Untitled Strategy',
    description: metadata?.description || 'No description provided',
    blocks,
    platform: metadata?.platform || TradingPlatform.MT5,
    assetClass: metadata?.assetClass || AssetClass.FOREX,
    created: new Date(),
    updated: new Date(),
    version: metadata?.version || '1.0.0',
    tags: metadata?.tags || [],
    isPublic: metadata?.isPublic || false, // Add this line
  };

  return JSON.stringify(strategy, null, 2);
};

// ── Import hard limits (deterministic, fail closed with clear errors). Real exported strategies are a
// few KB with a dozen blocks; these ceilings only reject pathological / tampered share payloads. ──
export const IMPORT_LIMITS = {
  maxJsonBytes: 5 * 1024 * 1024, // 5 MB source text
  maxBlocks: 500,
  maxConnectionsPerSide: 512,    // per block, inputs and outputs each
  maxIdLength: 256,
} as const;

/** Iterative scan of parsed-JSON data for JS object-machinery keys (__proto__ / constructor / prototype).
 *  JSON.parse itself never mutates prototypes, but such own-keys are a prototype-pollution payload for any
 *  downstream code that merges or re-assigns these objects — a legitimately exported strategy can never
 *  contain them (JSON.stringify of app state cannot emit them), so imports carrying them are rejected
 *  outright rather than sanitized. */
const findReservedKey = (root: unknown): string | null => {
  const stack: unknown[] = [root];
  const seen = new Set<object>();
  while (stack.length > 0) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object') continue;
    if (seen.has(cur)) continue;
    seen.add(cur);
    if (Array.isArray(cur)) {
      for (const v of cur) stack.push(v);
      continue;
    }
    for (const k of Object.keys(cur)) {
      if (k === '__proto__' || k === 'constructor' || k === 'prototype') return k;
      stack.push((cur as Record<string, unknown>)[k]);
    }
  }
  return null;
};

export const importStrategy = (
  jsonString: string
): { blocks: Block[]; metadata: StrategyMetadata } => {
  try {
    if (typeof jsonString !== 'string' || jsonString.length === 0) {
      throw new Error('Invalid strategy file: empty input');
    }
    if (jsonString.length > IMPORT_LIMITS.maxJsonBytes) {
      throw new Error(
        `Strategy file too large (${jsonString.length} chars > ${IMPORT_LIMITS.maxJsonBytes} max)`
      );
    }

    const strategy: Strategy = JSON.parse(jsonString);

    // Prototype-pollution guard: reject any payload smuggling JS object-machinery keys.
    const reserved = findReservedKey(strategy);
    if (reserved) {
      throw new Error(
        `Invalid strategy file: contains a forbidden object key '${reserved}'`
      );
    }

    // Validate required fields
    if (!strategy.blocks || !Array.isArray(strategy.blocks)) {
      throw new Error('Invalid strategy format: blocks array is missing');
    }
    if (strategy.blocks.length > IMPORT_LIMITS.maxBlocks) {
      throw new Error(
        `Strategy has too many blocks (${strategy.blocks.length} > ${IMPORT_LIMITS.maxBlocks} max)`
      );
    }

    // Validate each block
    strategy.blocks.forEach((block, index) => {
      if (!block.id) {
        throw new Error(`Block at index ${index} is missing an ID`);
      }
      if (typeof block.id !== 'string' || block.id.length > IMPORT_LIMITS.maxIdLength) {
        throw new Error(`Block at index ${index} has an invalid ID`);
      }
      if (!block.type) {
        throw new Error(`Block "${block.id}" is missing a type`);
      }
      if (
        !block.position ||
        typeof block.position.x !== 'number' ||
        typeof block.position.y !== 'number'
      ) {
        throw new Error(`Block "${block.id}" has invalid position coordinates`);
      }
      if (
        !block.connections ||
        !Array.isArray(block.connections.inputs) ||
        !Array.isArray(block.connections.outputs)
      ) {
        throw new Error(`Block "${block.id}" has invalid connections`);
      }
      if (
        block.connections.inputs.length > IMPORT_LIMITS.maxConnectionsPerSide ||
        block.connections.outputs.length > IMPORT_LIMITS.maxConnectionsPerSide
      ) {
        throw new Error(
          `Block "${block.id}" has too many connections (> ${IMPORT_LIMITS.maxConnectionsPerSide} per side)`
        );
      }
      // A carried TSL AST must be structurally sane (depth/node/cycle/reserved-name bounded) BEFORE it
      // can ever reach the interpreter. Same guard the interpreter itself enforces — this just surfaces
      // the error at import time with the block id attached.
      const tslAst = (block.data as Record<string, unknown> | undefined)?.tslAst;
      if (tslAst !== undefined && tslAst !== null) {
        const astErr = checkTslBlockShape(tslAst);
        if (astErr) {
          throw new Error(`Block "${block.id}" carries an invalid TSL AST: ${astErr}`);
        }
      }
    });

    // Validate connections
    strategy.blocks.forEach((block) => {
      block.connections.inputs.forEach((inputId) => {
        if (!strategy.blocks.some((b) => b.id === inputId)) {
          throw new Error(
            `Block "${block.id}" has invalid input connection to "${inputId}"`
          );
        }
      });

      block.connections.outputs.forEach((outputId) => {
        if (!strategy.blocks.some((b) => b.id === outputId)) {
          throw new Error(
            `Block "${block.id}" has invalid output connection to "${outputId}"`
          );
        }
      });
    });

    // Extract metadata
    const metadata: StrategyMetadata = {
      name: strategy.name || 'Untitled Strategy',
      description: strategy.description || 'No description provided',
      platform: strategy.platform || TradingPlatform.MT5,
      assetClass: strategy.assetClass || AssetClass.FOREX,
      version: strategy.version || '1.0.0',
      tags: strategy.tags || [],
    };

    return { blocks: strategy.blocks, metadata };
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON format');
    }
    throw error;
  }
};

export const validateConnections = (blocks: Block[]): void => {
  // Check for circular dependencies
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  const hasCycle = (blockId: string): boolean => {
    if (recursionStack.has(blockId)) return true;
    if (visited.has(blockId)) return false;

    visited.add(blockId);
    recursionStack.add(blockId);

    const block = blocks.find((b) => b.id === blockId);
    if (block) {
      for (const outputId of block.connections.outputs) {
        if (hasCycle(outputId)) return true;
      }
    }

    recursionStack.delete(blockId);
    return false;
  };

  blocks.forEach((block) => {
    if (hasCycle(block.id)) {
      throw new Error(
        `Circular dependency detected starting from block "${block.id}"`
      );
    }
  });
};

// Function to check if user is the author of a template
export const isTemplateAuthor = async (
  templateId: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('strategy_templates')
      .select('author_id')
      .eq('id', templateId)
      .single();

    if (error || !data) return false;

    return data.author_id === user.id;
  } catch (error) {
    console.error('Error checking template authorship:', error);
    return false;
  }
};
